import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, Copy, Send } from "lucide-react";
import {
  getOrderTrackingLink,
  resendOrderTrackingLink,
  type ResendTrackingLinkResult,
} from "@/api/orders";
import { IconButton } from "@/components/ui/IconButton";
import { Skeleton } from "@/components/ui/Skeleton";
import { toastError, toastSuccess, toastWarning } from "@/lib/toast";

interface Props {
  orderId: number;
  /** Whether the surrounding modal is open — gates the query. */
  enabled: boolean;
}

/**
 * "Customer tracking" block for the order detail modal. Shows the public
 * tracking URL with a copy-to-clipboard control and a "Resend to customer"
 * action when a token exists, and a neutral message otherwise.
 */
export function CustomerTrackingSection({ orderId, enabled }: Props) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["order", orderId, "tracking-link"],
    queryFn: () => getOrderTrackingLink(orderId),
    enabled: enabled && orderId > 0,
  });

  const resendMutation = useMutation({
    mutationFn: () => resendOrderTrackingLink(orderId),
    onSuccess: (result: ResendTrackingLinkResult) => {
      if (result.sent) {
        toastSuccess(t("orders.detail.tracking.sent"));
      } else {
        toastWarning(t("orders.detail.tracking.noContact"));
      }
    },
    onError: () => {
      toastError(t("orders.detail.tracking.error"));
    },
  });

  async function handleCopy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toastError(t("orders.detail.tracking.error"));
    }
  }

  return (
    <section>
      <div className="flex items-center gap-3 py-1">
        <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
          {t("orders.detail.tracking.title")}
        </span>
        <div className="h-px flex-1 bg-gray-100" />
      </div>

      {isLoading ? (
        <Skeleton className="mt-3 h-10 w-full" />
      ) : data?.available && data.tracking_url ? (
        // Owner, 2026-08-24: one row, icon-only actions. The labelled buttons
        // wrapped onto a second line and made a two-action block look like a
        // section of its own. `IconButton` keeps each action's name as BOTH an
        // aria-label and a tooltip, so dropping the visible text costs no clarity
        // — hover or focus still says «Копировать ссылку» / «Отправить клиенту
        // повторно» (the BUG-504/508 rule: an icon-only control is never nameless).
        <div className="mt-3 flex items-center gap-2">
          <input
            type="text"
            readOnly
            value={data.tracking_url}
            aria-label={t("orders.detail.tracking.link")}
            className="w-full min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700 outline-none"
          />
          <IconButton
            icon={
              copied ? (
                <Check
                  className="h-4 w-4 text-emerald-600"
                  aria-hidden="true"
                />
              ) : (
                <Copy className="h-4 w-4" aria-hidden="true" />
              )
            }
            label={
              copied
                ? t("orders.detail.tracking.copied")
                : t("orders.detail.tracking.copy")
            }
            variant="secondary"
            size="icon"
            onClick={() => void handleCopy(data.tracking_url!)}
            className="shrink-0"
          />
          <IconButton
            icon={<Send className="h-4 w-4" aria-hidden="true" />}
            label={
              resendMutation.isPending
                ? t("orders.detail.tracking.resending")
                : t("orders.detail.tracking.resend")
            }
            variant="secondary"
            size="icon"
            onClick={() => resendMutation.mutate()}
            disabled={resendMutation.isPending}
            className="shrink-0"
          />
        </div>
      ) : (
        <p className="mt-3 text-sm text-gray-500">
          {t("orders.detail.tracking.unavailable")}
        </p>
      )}
    </section>
  );
}
